import clsx from 'clsx';
import { useMemo } from 'react';
import { NumericFormat, NumberFormatValues } from 'react-number-format';
import { v4 } from 'uuid';

export default function RangeField({ className, 'data-testid': dataTestId, info: InfoComponent, label, name, optional, placeholders, values, onChange }: RangeFieldProps) {
  const idBase = useMemo(() => `${v4()}-${name}`, [name]);
  const idMin = `${idBase}-min`;
  const idMax = `${idBase}-max`;
  function getAllowedMin(fieldValues: NumberFormatValues) {
    const { floatValue } = fieldValues;
    return !floatValue || (floatValue >= 0 && floatValue <= 100 && (!values?.max || floatValue < values.max));
  }

  function getAllowedMax(fieldValues: NumberFormatValues) {
    const { floatValue } = fieldValues;
    return !floatValue || (floatValue >= 0 && floatValue <= 100);
  }

  function handleBlur(field: string) {
    if (values?.min !== undefined && values?.max !== undefined) {
      if (field === "min" && values.min > values.max) {
        onChange(
          {
            min: undefined,
            max: values.max
          },
          name
        );
      } else if (field === "max" && values.max < values.min) {
        onChange(
          {
            min: values.min,
            max: undefined
          },
          name
        );
      }
    }
  }

  function handleChange(field: string, fieldValues: NumberFormatValues) {
    onChange(
      {
        ...values,
        [field]: fieldValues.floatValue
      },
      name
    );
  }

  function focus(e: any) {
    if (e.target.tagName.toLowerCase() !== "input") {
      e.currentTarget.closest(".field-group").querySelector("input").focus();
    }
  }

  const active = values?.min !== undefined || values?.max !== undefined;

  const containerClasses = clsx("d-flex flex-column-reverse field-group", className, {
    "pdf-hide": !active,
    active,
    optional
  });

  return (
    <div className={containerClasses} data-testid={dataTestId}>
      <div className="field-control" onClick={focus}>
        <div className="d-flex flex-grow-1">
          <div className="field-prefix">Min:</div>
          <div className="flex-grow-1">
            <NumericFormat
              aria-label={`${label} - Min`}
              className="field-input"
              decimalScale={2}
              id={idMin}
              isAllowed={getAllowedMin}
              placeholder={placeholders?.min}
              suffix="%"
              value={values.min || ''}
              onBlur={() => handleBlur("min")}
              onValueChange={(values) => handleChange("min", values)}
            />
          </div>
        </div>
        <div className="d-flex flex-grow-1">
          <div className="field-prefix">Max:</div>
          <div className="flex-grow-1">
            <NumericFormat
              aria-label={`${label} - Max`}
              className="field-input"
              decimalScale={2}
              id={idMax}
              isAllowed={getAllowedMax}
              placeholder={placeholders?.max}
              suffix="%"
              value={values.max || ''}
              onBlur={() => handleBlur("max")}
              onValueChange={(values) => handleChange("max", values)}
            />
          </div>
        </div>
      </div>
      {label && (
        <div>
          <label className="field-label" htmlFor={idMin}>
            {optional && <span className="optional-label">(Optional)</span>}
            {label}
          </label>
          {InfoComponent && <InfoComponent />}
        </div>
      )}
    </div>
  );
}
